import { useEffect, useRef, useState } from "react";

// Turnstile Site Key
const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || "";

// Turnstile script 載入狀態
let scriptLoaded = false;
let scriptLoading = false;
const loadCallbacks: (() => void)[] = [];

function loadTurnstileScript(): Promise<void> {
  return new Promise((resolve) => {
    if (scriptLoaded) {
      resolve();
      return;
    }

    loadCallbacks.push(resolve);

    if (scriptLoading) {
      return;
    }

    scriptLoading = true;

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;

    script.onload = () => {
      scriptLoaded = true;
      loadCallbacks.forEach((cb) => cb());
      loadCallbacks.length = 0;
    };

    document.head.appendChild(script);
  });
}

interface TurnstileProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: (error: unknown) => void;
  theme?: "light" | "dark" | "auto";
  size?: "normal" | "compact";
}

export function Turnstile({
  onVerify,
  onExpire,
  onError,
  theme = "auto",
  size = "normal",
}: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) {
      return;
    }

    loadTurnstileScript().then(() => {
      setIsReady(true);
    });
  }, []);

  useEffect(() => {
    if (!isReady || !containerRef.current || !TURNSTILE_SITE_KEY) {
      return;
    }

    // 等待 turnstile 全局對象可用
    const renderWidget = () => {
      if (typeof window.turnstile === "undefined") {
        setTimeout(renderWidget, 100);
        return;
      }

      // 如果已經有 widget，先移除
      if (widgetIdRef.current) {
        window.turnstile.remove(widgetIdRef.current);
      }

      widgetIdRef.current = window.turnstile.render(containerRef.current!, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: onVerify,
        "expired-callback": onExpire,
        "error-callback": onError,
        theme,
        size,
      });
    };

    renderWidget();

    return () => {
      if (widgetIdRef.current && typeof window.turnstile !== "undefined") {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [isReady, onVerify, onExpire, onError, theme, size]);

  // 如果沒有 site key，不顯示任何東西（開發環境可跳過）
  if (!TURNSTILE_SITE_KEY) {
    return null;
  }

  return <div ref={containerRef} className="turnstile-widget" />;
}

// 重置 Turnstile widget（用於表單提交失敗後重試）
export function resetTurnstile() {
  try {
    if (typeof window.turnstile !== "undefined") {
      window.turnstile.reset();
    }
  } catch {
    // Widget 可能已被移除，忽略錯誤
  }
}

// 檢查 Turnstile 是否已配置
export function isTurnstileConfigured(): boolean {
  return !!TURNSTILE_SITE_KEY;
}

// TypeScript 類型定義
declare global {
  interface Window {
    turnstile: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: (error: unknown) => void;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "compact";
        }
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
    };
  }
}
